"use client";
import { useMemo } from "react";
import {
  IndustryHighlightsContentProps,
  IndustryHighlightsContent,
} from "./IndustryHighlightsContent";
import {
  HighlightsSearchWrapper,
  HighlightsSearchWrapperProps,
} from "./HighlightsSearchWrapper";
import useIndustryHighlights from "@/monarch/rules/industryHighlights/useIndustryHighlights";

interface IndustryHighlightsProps
  extends IndustryHighlightsContentProps,
    Omit<HighlightsSearchWrapperProps, "children"> {
  filterParams?: string;
}

export default function IndustryHighlights(props: IndustryHighlightsProps) {
  const {
    tagText,
    tagIcon,
    title,
    subject,
    tasksTitle,
    tasks,
    stats,
    cta,
    disclaimerText,
    disclaimerLinkText,
    disclaimerLink,
    disclaimerTextAfter,
    numberOfHits,
    filterParams,
    searchQuery,
    ...restProps
  } = props;

  // Try to get data from Monarch
  const { data: monarchData, isLoading: isMonarchLoading } =
    useIndustryHighlights();

  // Calculate final values using Monarch data if enabled is true, otherwise use props (from shortcode)
  const finalValues = useMemo(() => {
    const isMonarchActive = !isMonarchLoading && monarchData?.enabled === true;

    if (!isMonarchActive) {
      return {
        tagText,
        tagIcon,
        title,
        subject,
        tasksTitle,
        tasks,
        stats,
        cta,
        disclaimerText,
        disclaimerLinkText,
        disclaimerLink,
        disclaimerTextAfter,
        numberOfHits,
        filterParams: filterParams ?? searchQuery,
      };
    }

    return {
      tagText: monarchData.tagText ?? tagText,
      tagIcon: monarchData.tagIcon ?? tagIcon,
      title: monarchData.title ?? title,
      subject: monarchData.subject ?? subject,
      tasksTitle: monarchData.tasksTitle ?? tasksTitle,
      tasks: monarchData.tasks ?? tasks,
      stats: monarchData.stats ?? stats,
      cta: monarchData.cta ?? cta,
      disclaimerText: monarchData.disclaimerText ?? disclaimerText,
      disclaimerLinkText: monarchData.disclaimerLinkText ?? disclaimerLinkText,
      disclaimerLink: monarchData.disclaimerLink ?? disclaimerLink,
      disclaimerTextAfter:
        monarchData.disclaimerTextAfter ?? disclaimerTextAfter,
      numberOfHits: monarchData.numberOfHits ?? numberOfHits,
      filterParams: monarchData.filterParams ?? filterParams ?? searchQuery,
    };
  }, [
    isMonarchLoading,
    monarchData,
    tagText,
    tagIcon,
    title,
    subject,
    tasksTitle,
    tasks,
    stats,
    cta,
    disclaimerText,
    disclaimerLinkText,
    disclaimerLink,
    disclaimerTextAfter,
    numberOfHits,
    filterParams,
    searchQuery,
  ]);

  return (
    <HighlightsSearchWrapper searchQuery={finalValues.filterParams}>
      <IndustryHighlightsContent
        {...restProps}
        filterParams={finalValues.filterParams}
        tagText={finalValues.tagText}
        tagIcon={finalValues.tagIcon}
        title={finalValues.title}
        subject={finalValues.subject}
        tasksTitle={finalValues.tasksTitle}
        tasks={finalValues.tasks}
        stats={finalValues.stats}
        cta={finalValues.cta}
        disclaimerText={finalValues.disclaimerText}
        disclaimerLinkText={finalValues.disclaimerLinkText}
        disclaimerTextAfter={finalValues.disclaimerTextAfter}
        disclaimerLink={finalValues.disclaimerLink}
        numberOfHits={finalValues.numberOfHits}
      />
    </HighlightsSearchWrapper>
  );
}
